import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card } from '@ui'
import {
  Chart,
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js'
import { getCostReport } from '../api.js'

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip, Legend)

const GRANULARITIES = [
  { id: 'DAILY', label: 'Daily' },
  { id: 'WEEKLY', label: 'Weekly' },
  { id: 'MONTHLY', label: 'Monthly' },
]

const DEFAULT_LOOKBACK = { DAILY: 30, WEEKLY: 12, MONTHLY: 12 }
const TOP_N = 6
const PALETTE = [
  '#3B6D11', '#BA7517', '#5B8DEF', '#9B59B6', '#E67E73',
  '#2CA58D', '#C4A000', '#AD1457', '#455A64',
]

function fmt(currency, amount) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export function CostReport() {
  const [granularity, setGranularity] = useState('DAILY')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getCostReport(granularity, DEFAULT_LOOKBACK[granularity])
      .then((d) => { if (!cancelled) { setData(d); setError(null) } })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load cost report') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [granularity])

  if (error) return <p style={{ color: 'var(--danger-text)' }}>{error}</p>
  if (!data) return <p>Loading…</p>

  const dim = loading ? { opacity: 0.5, transition: 'opacity 0.15s' } : {}
  const total = data.buckets.reduce((s, b) => s + b.total, 0)
  const periodCount = data.buckets.length || 1
  const avg = total / periodCount

  return (
    <div style={dim}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Costs — {data.env}</h2>
          <p style={styles.sub}>
            AWS spend scoped to the <strong>{data.env}</strong> environment.
            Untagged shared costs are attributed at {Math.round(data.untaggedShare * 100)}%.
          </p>
        </div>
        <GranularityTabs value={granularity} onChange={setGranularity} />
      </div>

      <div style={styles.kpis}>
        <Kpi label={`Total (${data.lookback} ${granularity.toLowerCase()})`} value={fmt(data.currency, total)} />
        <Kpi label="Average per period" value={fmt(data.currency, avg)} />
        <Kpi label="Periods" value={String(periodCount)} />
      </div>

      <Card>
        <CostChart buckets={data.buckets} currency={data.currency} />
      </Card>

      <div style={{ marginTop: 16 }}>
        <Card>
          <ServiceTable buckets={data.buckets} currency={data.currency} />
        </Card>
      </div>
    </div>
  )
}

function GranularityTabs({ value, onChange }) {
  return (
    <div style={styles.tabs} role="tablist" aria-label="Granularity">
      {GRANULARITIES.map((g) => {
        const active = g.id === value
        return (
          <button
            key={g.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(g.id)}
            style={{ ...styles.tab, ...(active ? styles.tabActive : {}) }}
          >
            {g.label}
          </button>
        )
      })}
    </div>
  )
}

function Kpi({ label, value }) {
  return (
    <Card>
      <div style={{ padding: '4px 4px 0' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{value}</div>
      </div>
    </Card>
  )
}

function selectTopServices(buckets) {
  const totals = new Map()
  for (const b of buckets) {
    for (const s of b.services) {
      totals.set(s.name, (totals.get(s.name) || 0) + s.amount)
    }
  }
  const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1])
  const top = sorted.slice(0, TOP_N).map(([name]) => name)
  return top
}

function CostChart({ buckets, currency }) {
  const ref = useRef(null)
  const chartRef = useRef(null)

  const { labels, datasets } = useMemo(() => {
    const top = selectTopServices(buckets)
    const labels = buckets.map((b) => b.periodStart)
    const seriesByService = new Map(top.map((n) => [n, new Array(buckets.length).fill(0)]))
    const other = new Array(buckets.length).fill(0)
    buckets.forEach((b, i) => {
      for (const s of b.services) {
        if (seriesByService.has(s.name)) {
          seriesByService.get(s.name)[i] += s.amount
        } else {
          other[i] += s.amount
        }
      }
    })
    const datasets = top.map((name, idx) => ({
      label: name,
      data: seriesByService.get(name),
      backgroundColor: PALETTE[idx % PALETTE.length],
      stack: 'cost',
    }))
    if (other.some((v) => v > 0)) {
      datasets.push({
        label: 'Other',
        data: other,
        backgroundColor: '#B0B5BB',
        stack: 'cost',
      })
    }
    return { labels, datasets }
  }, [buckets])

  useEffect(() => {
    if (!ref.current) return
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null }
    chartRef.current = new Chart(ref.current, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${fmt(currency, ctx.parsed.y)}`,
            },
          },
        },
        scales: {
          x: { stacked: true },
          y: {
            stacked: true,
            ticks: { callback: (v) => fmt(currency, v) },
          },
        },
      },
    })
    return () => {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null }
    }
  }, [labels, datasets, currency])

  return (
    <div style={{ height: 320 }}>
      <canvas ref={ref} />
    </div>
  )
}

function ServiceTable({ buckets, currency }) {
  const totals = new Map()
  for (const b of buckets) {
    for (const s of b.services) {
      totals.set(s.name, (totals.get(s.name) || 0) + s.amount)
    }
  }
  const rows = [...totals.entries()].sort((a, b) => b[1] - a[1])
  const grand = rows.reduce((s, r) => s + r[1], 0) || 1
  return (
    <div>
      <h3 style={{ margin: '4px 4px 12px', fontSize: 14 }}>By service</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
            <th style={{ padding: '6px 4px' }}>Service</th>
            <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
            <th style={{ padding: '6px 4px', textAlign: 'right' }}>Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, amount]) => (
            <tr key={name} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '6px 4px' }}>{name}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>{fmt(currency, amount)}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                {((amount / grand) * 100).toFixed(1)}%
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={3} style={{ padding: 16, color: 'var(--text-secondary)' }}>No cost data for this range.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
    gap: 16,
  },
  title: { margin: 0, fontSize: 22 },
  sub: { margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 13, maxWidth: 640 },
  tabs: { display: 'inline-flex', gap: 4, background: 'var(--surface-alt)', padding: 4, borderRadius: 8 },
  tab: {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    color: 'var(--text-secondary)',
  },
  tabActive: { background: 'var(--surface)', color: 'var(--text-primary)', fontWeight: 600 },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 },
}
